import type { Room } from "building-types/rooms/roomTypes";
import type {
  AreaRule,
  BorderPosition,
  FloorAreaTakeoffConfig,
  RoomBorderPositions,
} from "building-types/rooms/takeoffTypes";
import { AreaRuleType, FloorFilter } from "building-types/rooms/takeoffTypes";
import type { RoomIdToRoomSetIndexLookup, RoomSetWithLevelId } from "doc/rooms/roomSetTypes";
import { isRoomSelected } from "doc/utils/floorAreaTakeoffTools";

export type RoomBoundaryTable = {
  [roomId: string]: {
    /** flag indicating included versus excluded */
    readonly includedRoom: boolean;
    readonly otherRoomBorderPosition?: BorderPosition;
    readonly exteriorRoomBorderPosition?: BorderPosition;
    readonly specificRoomBorderPositions?: RoomBorderPositions;
  };
};

type RoomSetBoundaryTable = {
  [roomSetId: string]: {
    readonly otherRoomBorderPosition?: BorderPosition;
    readonly exteriorRoomBorderPosition?: BorderPosition;
    readonly specificRoomBorderPositions?: RoomBorderPositions;
  };
};

export function getRoomsInFloorAreaTakeoff(
  roomsOnLevel: Room[],
  roomSetsPerLevel: RoomSetWithLevelId[],
  roomIdToRoomSetIndexLookup: RoomIdToRoomSetIndexLookup,
  takeoffConfig: FloorAreaTakeoffConfig,
  floorIndex: number,
): {
  includedRoomIds: { [roomId: string]: boolean };
  /** border position information for roomtypes assigned associatively by roomset and not explicitly by type  */
  roomBoundaryTable: RoomBoundaryTable;
} {
  const noIncludes = takeoffConfig.include.length === 0;
  const noExcludes = takeoffConfig.exclude.length === 0;

  const includedRoomIds: { [roomId: string]: boolean } = {};
  const excludedRoomIds: { [roomId: string]: boolean } = {};
  const roomBoundaryTable: RoomBoundaryTable = {};

  // if neither includes or excludes are defined, include all
  if (noIncludes && noExcludes) {
    for (const { id } of roomsOnLevel) {
      includedRoomIds[id] = true;
    }

    return { includedRoomIds, roomBoundaryTable };
  }

  const roomSetIncludedList: { [roomSetId: string]: boolean } = {};
  const roomSetExcludedList: { [roomSetId: string]: boolean } = {};

  const roomSetExcludedBoundaryTable: RoomSetBoundaryTable = {};
  const roomSetIncludedBoundaryTable: RoomSetBoundaryTable = {};

  // store border positions for roomsets
  for (const roomSet of roomSetsPerLevel) {
    const { id } = roomSet;
    let roomSetExcluded = false;

    const markRoomSetExcluded = (id: number, ruleEx: AreaRule) => {
      roomSetExcluded = true;
      roomSetExcludedList[id] = true;
      roomSetExcludedBoundaryTable[id] = {
        otherRoomBorderPosition: ruleEx.otherRoomBorderPosition,
        exteriorRoomBorderPosition: ruleEx.exteriorRoomBorderPosition,
        specificRoomBorderPositions: ruleEx.specificRoomBorderPositions,
      };
    };

    const markRoomSetIncluded = (id: number, ruleIn: AreaRule) => {
      roomSetIncludedList[id] = true;
      roomSetIncludedBoundaryTable[id] = {
        otherRoomBorderPosition: ruleIn.otherRoomBorderPosition,
        exteriorRoomBorderPosition: ruleIn.exteriorRoomBorderPosition,
        specificRoomBorderPositions: ruleIn.specificRoomBorderPositions,
      };
    };

    for (const room of roomSet.roomSet) {
      const { type } = room;
      for (const ruleEx of takeoffConfig.exclude) {
        if (!ruleEx.includeAnyInSameRoomSets) {
          continue;
        }
        if (ruleEx.appliesOnlyTo === FloorFilter.FIRST_FLOOR_ONLY && floorIndex !== 0) {
          continue;
        }
        if (ruleEx.appliesOnlyTo === FloorFilter.UPPER_FLOORS_ONLY && floorIndex < 1) {
          continue;
        }

        switch (ruleEx.type) {
          case AreaRuleType.BY_ROOM_SELECTOR: {
            if (isRoomSelected(room, ruleEx.roomSelectors)) {
              markRoomSetExcluded(id, ruleEx);
            }
            break;
          }
          case AreaRuleType.BY_ROOM_TYPE: {
            if (ruleEx?.roomTypes && ruleEx?.roomTypes[type]) {
              markRoomSetExcluded(id, ruleEx);
            }
            break;
          }
        }
      }

      // if excluded, block roomset inclusion
      if (roomSetExcluded) {
        continue;
      }

      for (const ruleIn of takeoffConfig.include) {
        if (!ruleIn.includeAnyInSameRoomSets) {
          continue;
        }
        if (ruleIn.appliesOnlyTo === FloorFilter.FIRST_FLOOR_ONLY && floorIndex !== 0) {
          continue;
        }
        if (ruleIn.appliesOnlyTo === FloorFilter.UPPER_FLOORS_ONLY && floorIndex < 1) {
          continue;
        }

        switch (ruleIn.type) {
          case AreaRuleType.BY_ROOM_SELECTOR: {
            if (isRoomSelected(room, ruleIn.roomSelectors)) {
              markRoomSetIncluded(id, ruleIn);
            }
            break;
          }
          case AreaRuleType.BY_ROOM_TYPE: {
            if (ruleIn?.roomTypes && ruleIn?.roomTypes[type]) {
              markRoomSetIncluded(id, ruleIn);
            }
            break;
          }
        }
      }
    }
  }

  for (const room of roomsOnLevel) {
    const roomSetId = roomIdToRoomSetIndexLookup[room.id];

    // assign border positions from roomset to room
    if (roomSetExcludedList[roomSetId]) {
      excludedRoomIds[room.id] = true;

      const result = roomSetExcludedBoundaryTable[roomSetId];
      if (result) {
        const { otherRoomBorderPosition, exteriorRoomBorderPosition, specificRoomBorderPositions } = result;
        roomBoundaryTable[room.id] = {
          includedRoom: false,
          otherRoomBorderPosition,
          exteriorRoomBorderPosition,
          specificRoomBorderPositions,
        };
      }

      continue;
    }

    if (roomSetIncludedList[roomSetId]) {
      includedRoomIds[room.id] = true;

      const result = roomSetIncludedBoundaryTable[roomSetId];
      if (result) {
        const { otherRoomBorderPosition, exteriorRoomBorderPosition, specificRoomBorderPositions } = result;
        roomBoundaryTable[room.id] = {
          includedRoom: true,
          otherRoomBorderPosition,
          exteriorRoomBorderPosition,
          specificRoomBorderPositions,
        };
      }

      continue;
    }

    let roomExcluded = false;
    for (const ruleEx of takeoffConfig.exclude) {
      if (ruleEx.includeAnyInSameRoomSets) {
        continue;
      }
      if (ruleEx.appliesOnlyTo === FloorFilter.FIRST_FLOOR_ONLY && floorIndex !== 0) {
        continue;
      }
      if (ruleEx.appliesOnlyTo === FloorFilter.UPPER_FLOORS_ONLY && floorIndex < 1) {
        continue;
      }

      switch (ruleEx.type) {
        case AreaRuleType.BY_ROOM_SELECTOR: {
          if (isRoomSelected(room, ruleEx.roomSelectors)) {
            excludedRoomIds[room.id] = true;
            roomExcluded = true;
          }
          break;
        }
        case AreaRuleType.BY_ROOM_TYPE: {
          if (ruleEx?.roomTypes && ruleEx?.roomTypes[room.type]) {
            excludedRoomIds[room.id] = true;
            roomExcluded = true;
          }
          break;
        }
      }
    }

    // if excluded, block any subsequent inclusion
    if (roomExcluded) {
      continue;
    }

    let roomIncluded = false;
    for (const ruleIn of takeoffConfig.include) {
      if (ruleIn.includeAnyInSameRoomSets) {
        continue;
      }
      if (ruleIn.appliesOnlyTo === FloorFilter.FIRST_FLOOR_ONLY && floorIndex !== 0) {
        continue;
      }
      if (ruleIn.appliesOnlyTo === FloorFilter.UPPER_FLOORS_ONLY && floorIndex < 1) {
        continue;
      }

      switch (ruleIn.type) {
        case AreaRuleType.BY_ROOM_SELECTOR: {
          if (isRoomSelected(room, ruleIn.roomSelectors)) {
            includedRoomIds[room.id] = true;
            roomIncluded = true;
          }
          break;
        }
        case AreaRuleType.BY_ROOM_TYPE: {
          if (ruleIn?.roomTypes && ruleIn?.roomTypes[room.type]) {
            includedRoomIds[room.id] = true;
            roomIncluded = true;
          }
          break;
        }
      }
    }

    if (roomIncluded) {
      continue;
    }

    // if there aren't any inclusions declared, include all that have not been explicitly excluded
    if (noIncludes) {
      includedRoomIds[room.id] = true;
      continue;
    }

    // if there are inclusions, exclude all that have not been explicity included or excluded
    excludedRoomIds[room.id] = true;
  }

  const levelRoomCount = roomsOnLevel.length;
  const includedRoomCount = Object.keys(includedRoomIds).length;
  const excludedRoomCount = Object.keys(excludedRoomIds).length;

  if (!(levelRoomCount === includedRoomCount + excludedRoomCount)) {
    console.assert(false, `floor area takeoff level room count of ${levelRoomCount} does not equal included - ${includedRoomCount} plus excluded- ${excludedRoomCount} rooms.`);
  }

  return { includedRoomIds, roomBoundaryTable };
}
